import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

function useVisible(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const pillars = [
  {
    num: '01',
    icon: 'ri-seedling-line',
    title: 'Personal Growth',
    body: 'We invest in our team members\' development at every level — from front desk to executive leadership — because when our people grow, so do our properties.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
  },
  {
    num: '02',
    icon: 'ri-team-line',
    title: 'Collaboration Over Competition',
    body: 'We operate as one team across all properties, sharing knowledge, best practices, and resources to lift every partner\'s performance.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
  },
  {
    num: '03',
    icon: 'ri-shield-check-line',
    title: 'Accountability',
    body: 'We own our results. When challenges arise, we face them directly, learn from them, and find better paths forward — for our owners and our guests.',
    color: 'text-sky-400',
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/20',
  },
  {
    num: '04',
    icon: 'ri-heart-line',
    title: 'Inclusion',
    body: 'Every voice matters at 360. We build teams that reflect the communities we serve and create environments where people feel respected, heard, and valued.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
  },
  {
    num: '05',
    icon: 'ri-community-line',
    title: 'Service to Community',
    body: 'Great hospitality extends beyond the hotel walls. We believe businesses can be a force for good in the communities where we operate.',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
  },
];

const commandments = [
  { icon: 'ri-user-smile-line', text: 'Treat every guest as if they are a guest in your own home.' },
  { icon: 'ri-hand-heart-line', text: 'Lead with empathy — in every interaction, every day.' },
  { icon: 'ri-bar-chart-line', text: 'Own your numbers. Know your property\'s performance at all times.' },
  { icon: 'ri-speak-line', text: 'Communicate openly, honestly, and with respect.' },
  { icon: 'ri-lightbulb-flash-line', text: 'Bring solutions, not just problems. Be the answer.' },
  { icon: 'ri-recycle-line', text: 'Leave every space — and every person — better than you found them.' },
];

function CultureHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative h-[540px] md:h-[660px] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_ba4d111c57cad659d97f2011df3d6d52_65314f2e.jpg"
          alt="360 Hospitality Group Culture"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/55 to-slate-950/15"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/40 to-transparent"></div>
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pb-16 md:pb-24">
        <div className={`max-w-2xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-400"></div>
            <span className="text-xs font-medium text-amber-400 uppercase tracking-[0.25em]">Our Culture</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-light text-white leading-tight tracking-tight mb-4">
            A Culture Built<br />
            <span className="text-amber-400 italic">on People</span>
          </h1>
          <p className="text-lg text-gray-300 font-light leading-relaxed">
            Empowering Our Team. Enhancing Every Stay.
          </p>
        </div>
      </div>
    </section>
  );
}

function CultureIntro() {
  const { ref, visible } = useVisible();
  const stats = [
    { value: '100+', label: 'Team Members Across Portfolio' },
    { value: '25+', label: 'Years of Leadership Experience' },
    { value: '4+', label: 'Florida Markets' },
    { value: '98%', label: 'Team Retention Rate' },
  ];

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text side */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="h-px w-8 bg-amber-500"></div>
              <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">Who We Are Inside</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-8">
              Culture Is the<br />
              <span className="text-amber-500 italic">Foundation</span>
            </h2>
            <p className="text-base text-slate-600 font-light leading-relaxed mb-8">
              At 360 Hospitality Group, our culture is the foundation of everything we do. We foster a workplace that promotes personal growth, open collaboration, and service to others. From our corporate office to every property we manage, our values shape how we treat guests, partners, and each other — because great hospitality starts from within.
            </p>
            <div className="flex items-start space-x-4 p-5 bg-amber-50 rounded-xl border border-amber-100">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg shrink-0 mt-0.5">
                <i className="ri-double-quotes-l text-xl text-amber-600"></i>
              </div>
              <p className="text-sm text-amber-900 font-light leading-relaxed italic">
                "Every 360 team member carries these principles — in their role, in their interactions, and in their commitment to our guests and partners."
              </p>
            </div>
          </div>

          {/* Stats grid */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s, i) => (
                <div
                  key={i}
                  className="bg-slate-950 rounded-2xl p-8 flex flex-col justify-between"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <p className="text-4xl font-light text-amber-400 mb-3">{s.value}</p>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-widest leading-snug">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CulturePillars() {
  const { ref, visible } = useVisible();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Five Pillars</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">
              What We Stand For.<br />
              <span className="text-amber-400 italic">Every Single Day.</span>
            </h2>
            <p className="text-base text-gray-400 font-light max-w-sm leading-relaxed">
              These five pillars define how we show up — for our team, our owners, and every guest who walks through our doors.
            </p>
          </div>
        </div>

        {/* Pillars — first 3 in a row, last 2 centered */}
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {pillars.slice(0, 3).map((p, i) => (
              <div
                key={i}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className={`relative group rounded-2xl border p-8 transition-all duration-500 cursor-default ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                } ${hovered === i ? `bg-slate-800/80 ${p.border}` : 'bg-slate-900 border-slate-800'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="text-xs font-medium text-gray-600 tracking-[0.3em] mb-5 block">{p.num}</span>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 transition-all duration-300 ${p.bg}`}>
                  <i className={`${p.icon} text-2xl ${p.color}`}></i>
                </div>
                <h3 className={`text-lg font-medium mb-3 transition-colors duration-300 ${hovered === i ? p.color : 'text-white'}`}>
                  {p.title}
                </h3>
                <p className="text-sm text-gray-400 font-light leading-relaxed">{p.body}</p>
                <div className={`absolute bottom-0 left-0 h-0.5 rounded-b-2xl transition-all duration-500 ${p.color.replace('text-', 'bg-')} ${hovered === i ? 'w-full' : 'w-0'}`}></div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4 md:max-w-2xl md:mx-auto lg:max-w-none lg:grid-cols-2 xl:w-2/3 xl:mx-auto">
            {pillars.slice(3).map((p, i) => (
              <div
                key={i + 3}
                onMouseEnter={() => setHovered(i + 3)}
                onMouseLeave={() => setHovered(null)}
                className={`relative group rounded-2xl border p-8 transition-all duration-500 cursor-default ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                } ${hovered === i + 3 ? `bg-slate-800/80 ${p.border}` : 'bg-slate-900 border-slate-800'}`}
                style={{ transitionDelay: `${(i + 3) * 80}ms` }}
              >
                <span className="text-xs font-medium text-gray-600 tracking-[0.3em] mb-5 block">{p.num}</span>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 transition-all duration-300 ${p.bg}`}>
                  <i className={`${p.icon} text-2xl ${p.color}`}></i>
                </div>
                <h3 className={`text-lg font-medium mb-3 transition-colors duration-300 ${hovered === i + 3 ? p.color : 'text-white'}`}>
                  {p.title}
                </h3>
                <p className="text-sm text-gray-400 font-light leading-relaxed">{p.body}</p>
                <div className={`absolute bottom-0 left-0 h-0.5 rounded-b-2xl transition-all duration-500 ${p.color.replace('text-', 'bg-')} ${hovered === i + 3 ? 'w-full' : 'w-0'}`}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CommandmentsCard() {
  const { ref, visible } = useVisible();

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left — image */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative rounded-2xl overflow-hidden h-[480px]">
              <img
                src="https://readdy.ai/api/search-image?query=Hotel%20team%20members%20in%20a%20morning%20briefing%2C%20standing%20in%20a%20circle%20in%20a%20bright%20hotel%20lobby%2C%20professional%20uniforms%2C%20warm%20natural%20light%2C%20collaborative%20atmosphere%2C%20Florida%20luxury%20hotel%20interior%2C%20editorial%20photography%2C%20genuine%20smiles%2C%20diverse%20team%2C%20amber%20and%20cream%20tones%2C%20motivational%20team%20huddle&width=800&height=900&seq=culture-commandments-360&orientation=portrait"
                alt="360 Hospitality Group Team Principles"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-amber-500 rounded-xl px-5 py-4">
                  <p className="text-white text-sm font-medium leading-relaxed">
                    "Every 360 team member carries these principles — in their role, in their interactions, and in their commitment to our guests and partners."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — commandments */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="h-px w-8 bg-amber-500"></div>
              <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">The 360 Way</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-4">
              Principles We<br />
              <span className="text-amber-500 italic">Live By</span>
            </h2>
            <p className="text-sm text-slate-500 font-light leading-relaxed mb-10">
              These aren't posted on a wall. They're practiced in every shift, every conversation, and every decision made across our portfolio.
            </p>

            <div className="space-y-4">
              {commandments.map((c, i) => (
                <div
                  key={i}
                  className={`flex items-start space-x-4 p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-amber-200 hover:bg-amber-50/30 transition-all duration-300 cursor-default ${
                    visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                  }`}
                  style={{ transitionDelay: `${200 + i * 70}ms` }}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                    <i className={`${c.icon} text-base text-amber-600`}></i>
                  </div>
                  <p className="text-sm text-slate-700 font-light leading-relaxed pt-1.5">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CulturePhotoStrip() {
  const { ref, visible } = useVisible();
  const photos = [
    {
      src: 'https://readdy.ai/api/search-image?query=Hotel%20front%20desk%20team%20member%20warmly%20greeting%20a%20guest%2C%20professional%20uniform%2C%20bright%20modern%20hotel%20lobby%2C%20warm%20amber%20lighting%2C%20genuine%20smile%2C%20Florida%20luxury%20hotel%2C%20candid%20hospitality%20photography%2C%20natural%20interaction&width=400&height=500&seq=culture-strip-1&orientation=portrait',
      caption: 'Guest First',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Hotel%20management%20team%20in%20a%20strategy%20meeting%20around%20a%20modern%20conference%20table%2C%20laptops%20and%20documents%2C%20professional%20attire%2C%20bright%20office%20with%20large%20windows%2C%20collaborative%20atmosphere%2C%20Florida%20hospitality%20company%2C%20editorial%20photography&width=400&height=500&seq=culture-strip-2&orientation=portrait',
      caption: 'Team Strategy',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Hotel%20housekeeping%20team%20member%20carefully%20preparing%20a%20luxury%20hotel%20room%2C%20crisp%20white%20linens%2C%20attention%20to%20detail%2C%20professional%20uniform%2C%20warm%20natural%20light%2C%20Florida%20boutique%20hotel%2C%20editorial%20photography%2C%20pride%20in%20work&width=400&height=500&seq=culture-strip-3&orientation=portrait',
      caption: 'Excellence in Detail',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Hotel%20team%20celebrating%20a%20milestone%20together%20in%20a%20hotel%20lobby%2C%20group%20photo%2C%20diverse%20professionals%2C%20warm%20smiles%2C%20professional%20attire%2C%20Florida%20luxury%20hotel%20interior%2C%20amber%20and%20cream%20tones%2C%20genuine%20celebration%20moment%2C%20editorial%20photography&width=400&height=500&seq=culture-strip-4&orientation=portrait',
      caption: 'Celebrating Wins',
    },
  ];

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-5">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-500 uppercase tracking-[0.25em]">Life at 360</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight">
            Culture in Action
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((photo, i) => (
            <div
              key={i}
              className={`group relative rounded-2xl overflow-hidden h-72 md:h-80 transition-all duration-700 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <img
                src={photo.src}
                alt={photo.caption}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <p className="text-xs font-medium text-amber-400 uppercase tracking-widest">{photo.caption}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CultureCTA() {
  const { ref, visible } = useVisible();
  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">Join the Team</span>
            <div className="h-px w-8 bg-amber-500"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 leading-tight mb-6">
            Be Part of Something<br />
            <span className="text-amber-500 italic">Worth Showing Up For</span>
          </h2>
          <p className="text-base text-slate-500 font-light leading-relaxed mb-10 max-w-xl mx-auto">
            Whether you're a hospitality professional looking for your next chapter or an owner looking for a partner who actually cares — 360 is built for people who take pride in what they do.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/about"
              className="group px-8 py-4 rounded-full bg-[#1a2b3c] hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 text-white text-base font-medium tracking-wide transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Learn More About 360</span>
              <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-1"></i>
            </Link>
            <Link
              to="/"
              className="group px-8 py-4 rounded-full border border-slate-300 text-slate-700 text-base font-light tracking-wide hover:border-amber-400 hover:text-amber-600 transition-all duration-300 whitespace-nowrap flex items-center space-x-2"
            >
              <span>Schedule a Performance Assessment</span>
              <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CulturePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <CultureHero />
      <CultureIntro />
      <CulturePillars />
      <CommandmentsCard />
      <CulturePhotoStrip />
      <CultureCTA />
      <Footer />
    </div>
  );
}
